import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { uniq } from 'underscore';
import {
  BinderClassificationDtoV2,
  BinderDto,
  BinderManagementDTO,
  ClassificationCodeByIdDto,
  ClassificationCodeDto,
  DropdownClassificationCode,
  FolderDto,
  FolderManagementDTO,
  SearchFoldersClassificationParams,
} from '../interfaces';
import { FardeCreationBody } from 'src/app/shared/models/classement/farde-creation';
import { ChemiseCreateUpdate } from 'src/app/shared/models/classement/chemise-create-update';
import { environment } from 'src/environments/environment';

export interface ClassificationLookupMaps {
  classementMap: Map<number, string>;
  classeurMap: Map<number, string>;
  chemiseMap: Map<number, string>;
}

@Injectable({
  providedIn: 'root',
})
export class ClassificationsService {
  private iAdminURL;
  private classificationLookupMaps$: Observable<ClassificationLookupMaps>;

  constructor(private http: HttpClient) {
    const config = environment;
    this.iAdminURL = config.iAdminApiBaseURL;
  }

  /**
   * Returns Maps resolving classementId/classeurId/chemiseId to their label (code for classement, description for classeur/chemise).
   * Built from fetchFoldersClassificationV2() and cached for the app lifetime (the classification tree changes rarely).
   */
  public getClassificationLookupMaps(): Observable<ClassificationLookupMaps> {
    if (!this.classificationLookupMaps$) {
      this.classificationLookupMaps$ = this.fetchFoldersClassificationV2().pipe(
        map((classements) => {
          const classementMap = new Map<number, string>();
          const classeurMap = new Map<number, string>();
          const chemiseMap = new Map<number, string>();
          (classements || []).forEach((classement) => {
            classementMap.set(classement.id, [classement.code, classement.description].filter(Boolean).join(' '));
            (classement.classeurs || []).forEach((classeur) => {
              classeurMap.set(classeur.id, classeur.description);
              (classeur.chemises || []).forEach((chemise) => {
                chemiseMap.set(chemise.id, chemise.description);
              });
            });
          });
          return { classementMap, classeurMap, chemiseMap };
        }),
        shareReplay(1)
      );
    }
    return this.classificationLookupMaps$;
  }

  public fetchFoldersClassificationV2(
    searchFoldersClassificationParams?: SearchFoldersClassificationParams
  ): Observable<BinderClassificationDtoV2[]> {
    return this.http.get<BinderClassificationDtoV2[]>(`${this.iAdminURL}/Global/ClassementDossiers/SearchV2`, {
      params: searchFoldersClassificationParams ? { ...searchFoldersClassificationParams } : {},
    });
  }
  public fetchFoldersClassificationById(id: number): Observable<ClassificationCodeByIdDto> {
    return this.http.get<ClassificationCodeByIdDto>(`${this.iAdminURL}/Global/ClassementDossiers/${id}`);
  }

  public fetchFolders(params?: { serviceId?: number; classementId?: number }): Observable<FolderDto[]> {
    return this.http.get<FolderDto[]>(`${this.iAdminURL}/Global/Classeurs`, { params: params ? { ...params } : {} });
  }
  public postFolder(data: FardeCreationBody): Observable<FolderManagementDTO> {
    return this.http.post<FolderManagementDTO>(`${this.iAdminURL}/Management/Fardes`, data);
  }

  public fetchBinders(folderId: number): Observable<BinderDto[]> {
    return this.http.get<BinderDto[]>(`${this.iAdminURL}/Global/Chemises`, { params: { classeurId: folderId } });
  }

  public postBinder(data: ChemiseCreateUpdate): Observable<BinderManagementDTO> {
    return this.http.post<BinderManagementDTO>(`${this.iAdminURL}/Management/Chemises`, data);
  }

  public fetchClassificationCodes(active = true): Observable<ClassificationCodeDto[]> {
    return this.http.get<ClassificationCodeDto[]>(`${this.iAdminURL}/Global/Classements/Search?actifOnly=${active}`);
  }

  public fetchMappedClassificationCodesToPrimeNGDropdowns(uniqueValues = false): Observable<DropdownClassificationCode[]> {
    return this.fetchClassificationCodes().pipe(
      map((res: ClassificationCodeDto[]) => {
        let mappedClassificationCodeArray: DropdownClassificationCode[] = [];

        res.forEach((classificationCode: ClassificationCodeDto) => {
          let mappedClassificationCode: DropdownClassificationCode = {} as DropdownClassificationCode;
          mappedClassificationCode = {
            ...classificationCode,
            label: `${classificationCode.code}${classificationCode.description ? ' (' + classificationCode.description + ')' : ''}`,
            value: classificationCode.id,
          };
          mappedClassificationCodeArray.push(mappedClassificationCode);
        });

        if (uniqueValues) {
          mappedClassificationCodeArray = uniq(mappedClassificationCodeArray, 'value');
        }

        return mappedClassificationCodeArray;
      })
    );
  }

  public insertNewClassificationCode(newFolder: {
    code: string;
    description: string;
    actif: boolean;
  }): Observable<{ id: number; code: string; description: string; actif: boolean }> {
    return this.http.post<{ id: number; code: string; description: string; actif: boolean }>(`${this.iAdminURL}/Management/Classements`, {
      ...newFolder,
    });
  }
}
